﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { api, type ObjectRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../hooks/useActionMenuAutoClose";

type StatusOption = "" | "aktiv" | "inaktiv";
type DigitalOnlyOption = "" | "1" | "0";
type StandardStimmrechtsartOption = "" | "kopf" | "stimmrecht" | "mea";
type ObjectSortField =
  | "objektnummer"
  | "objektname"
  | "anzahl_einheiten"
  | "anzahl_eigentuemer"
  | "anzahl_nicht_digital_erreichbar"
  | "stimmrecht_summe"
  | "mea_summe";

type DeleteConfirmState = {
  id: string;
  label: string;
  code: string;
  input: string[];
  error: string;
} | null;

type ObjectsPageProps = {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

const formatNumberDE = (value: number) => new Intl.NumberFormat("de-DE").format(value);

export const ObjectsPage = ({ canCreate = false, canUpdate = false, canDelete = false }: ObjectsPageProps) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState<StatusOption>("aktiv");
  const [digitalOnly, setDigitalOnly] = useState<DigitalOnlyOption>("");
  const [sortBy, setSortBy] = useState<ObjectSortField>("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [form, setForm] = useState({
    objektnummer: "",
    objektname: "",
    strasse: "",
    strasse2: "",
    plz: "",
    ort: "",
    standard_stimmrechtsart: "" as StandardStimmrechtsartOption,
    status: "" as StatusOption
  });
  const [editForm, setEditForm] = useState<null | {
    id: string;
    objektnummer: string;
    objektname: string;
    strasse: string;
    strasse2: string;
    plz: string;
    ort: string;
    standard_stimmrechtsart: StandardStimmrechtsartOption;
    status: "aktiv" | "inaktiv";
  }>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 450);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data, isError, isFetching } = useQuery({
    queryKey: ["objects", page, pageSize, debouncedSearch, status, digitalOnly, sortBy, sortDir],
    queryFn: () =>
      api.objects({
        page,
        pageSize,
        search: debouncedSearch,
        status: status || undefined,
        digitalOnly: digitalOnly || undefined,
        sortBy,
        sortDir
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 15_000
  });
  const { data: activeResolutions } = useQuery({
    queryKey: ["active-resolutions-lock"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 500, status: "aktiv" })
  });
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-objects"],
    queryFn: () => api.uiSettings()
  });
  const faciliooEnabled = uiSettings?.management?.faciliooEnabled !== false;
  const { data: faciliooSyncStatus } = useQuery({
    queryKey: ["facilioo-sync-status-objects"],
    queryFn: () => api.getFaciliooSyncStatus(),
    enabled: faciliooEnabled,
    staleTime: 30_000
  });

  const lockedObjectIds = useMemo(() => new Set((activeResolutions?.data ?? []).map((r) => r.objectId)), [activeResolutions?.data]);

  useEffect(() => {
    setPage(1);
  }, [search, status, digitalOnly, sortBy, sortDir, pageSize]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [page, search, status, digitalOnly, sortBy, sortDir, showCreate, editForm]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) setIsActionMenuHovered(false);
  }, [openActionMenuId]);

  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId
  });

  useEffect(() => {
    // Nur korrigieren, wenn die Server-Meta wirklich geladen ist.
    // Sonst springt die UI beim Seitenwechsel kurz auf Seite 1 zurück.
    const total = data?.meta?.total;
    const serverPage = data?.meta?.page;
    if (typeof total !== "number" || typeof serverPage !== "number") return;
    if (serverPage !== page) return;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (page > totalPages && (data?.data?.length ?? 0) === 0) {
      setPage(totalPages);
    }
  }, [data?.meta?.total, data?.meta?.page, data?.data?.length, page, pageSize]);

  const createMutation = useMutation({
    mutationFn: () =>
      api.createObject({
        objektnummer: form.objektnummer,
        objektname: form.objektname,
        strasse: form.strasse,
        strasse2: form.strasse2.trim() || undefined,
        plz: form.plz,
        ort: form.ort,
        standard_stimmrechtsart: form.standard_stimmrechtsart || undefined,
        status: form.status as "aktiv" | "inaktiv"
      }),
    onSuccess: async () => {
      setCreateError("");
      setShowCreate(false);
      setForm({ objektnummer: "", objektname: "", strasse: "", strasse2: "", plz: "", ort: "", standard_stimmrechtsart: "", status: "" });
      await queryClient.invalidateQueries({ queryKey: ["objects"] });
      setPage(1);
    },
    onError: (error) => setCreateError(error instanceof Error ? error.message : "Anlage fehlgeschlagen")
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!editForm) throw new Error("Kein Datensatz ausgewählt");
      return api.updateObject(editForm.id, {
        objektnummer: editForm.objektnummer,
        objektname: editForm.objektname,
        strasse: editForm.strasse,
        strasse2: editForm.strasse2.trim() || undefined,
        plz: editForm.plz,
        ort: editForm.ort,
        standard_stimmrechtsart: editForm.standard_stimmrechtsart || undefined,
        status: editForm.status
      });
    },
    onSuccess: async () => {
      setEditError("");
      setEditForm(null);
      await queryClient.invalidateQueries({ queryKey: ["objects"] });
    },
    onError: (error) => setEditError(error instanceof Error ? error.message : "Bearbeitung fehlgeschlagen")
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deactivateObject(id),
    onSuccess: async () => {
      setDeleteConfirm(null);
      setActionError("");
      await queryClient.invalidateQueries({ queryKey: ["objects"] });
    },
    onError: (error) => setActionError(error instanceof Error ? error.message : "Löschen fehlgeschlagen")
  });

  const toggleSort = (field: ObjectSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };

  const sortLabel = (field: ObjectSortField, label: ReactNode) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={sortBy === field ? `Sortierung: ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );

  const columns = useMemo(
    () => [
      {
        key: "objektnummer",
        label: "Objekt‑Nr",
        align: "center" as const,
        width: 92,
        render: (r: ObjectRow) => (
          <Link to={`/objects/${r.id}`} className="badge object-ref" style={{ textDecoration: "none" }}>
            {r.objektnummer}
          </Link>
        )
      },
      {
        key: "objektname",
        label: sortLabel("objektname", "Objektname"),
        render: (r: ObjectRow) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>{r.objektname}</span>
            {Number(r.anzahl_nicht_digital_erreichbar ?? 0) === 0 ? (
              <span className="badge aktiv" style={{ fontSize: 11, padding: "2px 8px" }}>
                100 % digital
              </span>
            ) : null}
          </span>
        )
      },
      { key: "anzahl_einheiten", label: sortLabel("anzahl_einheiten", <>Einheiten<br />Objekt</>), align: "center" as const },
      { key: "anzahl_eigentuemer", label: sortLabel("anzahl_eigentuemer", <>Eigentümer im<br />Objekt | Köpfe</>), align: "center" as const },
      { key: "anzahl_nicht_digital_erreichbar", label: sortLabel("anzahl_nicht_digital_erreichbar", <>Nicht digital<br />erreichbar</>), align: "center" as const },
      {
        key: "stimmrecht_summe",
        label: <>Stimmrechte<br />Objekt</>,
        width: 154,
        align: "center" as const,
        render: (r: ObjectRow) => {
          const value = Number(r.stimmrecht_summe ?? 0);
          return value > 0 ? value : "-";
        }
      },
      { key: "mea_summe", label: <>MEA<br />Objekt</>, width: 126, align: "center" as const },
      { key: "status", label: "Status", align: "center" as const, render: (r: ObjectRow) => <span className={`badge ${r.status}`}>{r.status}</span> },
      {
        key: "actions",
        label: "Aktionen",
        render: (r: ObjectRow) => {
          if (!canUpdate && !canDelete) return <span style={{ color: "var(--text-soft)" }}>-</span>;
          const locked = lockedObjectIds.has(r.id);
          return (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {canUpdate ? (
                <button
                  onClick={() => openEditForm(r)}
                  disabled={locked}
                  title={locked ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined}
                >
                  Bearbeiten
                </button>
              ) : null}
              {canDelete ? (
                <button
                  onClick={() => {
                    if (locked) {
                      setActionError("Objekt ist wegen aktivem Umlaufbeschluss gesperrt.");
                      return;
                    }
                    setDeleteConfirm({
                      id: r.id,
                      label: `${r.objektnummer} - ${r.objektname}`,
                      code: String(Math.floor(1000 + Math.random() * 9000)),
                      input: ["", "", "", ""],
                      error: ""
                    });
                  }}
                  disabled={locked || deleteMutation.isPending}
                  title={locked ? "Löschen bei aktivem Umlaufbeschluss gesperrt" : undefined}
                >
                  Löschen
                </button>
              ) : null}
              {locked && (canUpdate || canDelete) ? (
                <span className="lock-indicator" title="Gesperrt wegen aktivem Umlaufbeschluss">
                  {"\uD83D\uDD12"}
                </span>
              ) : null}
            </div>
          );
        }
      }
    ],
    [canDelete, canUpdate, deleteMutation.isPending, lockedObjectIds, sortBy, sortDir]
  );

  const canCreateForm =
    form.objektnummer.trim() && form.objektname.trim() && form.strasse.trim() && form.plz.trim() && form.ort.trim() && form.status;
  const canEdit = editForm && editForm.objektnummer.trim() && editForm.objektname.trim() && editForm.strasse.trim() && editForm.plz.trim() && editForm.ort.trim();
  const isObjectFormActive = showCreate || Boolean(editForm);
  const hasNoObjects = (data?.meta?.total ?? 0) === 0 && !isFetching;
  const lastSyncFailed = faciliooEnabled && faciliooSyncStatus?.lastRun?.ok === false;
  const lastSyncTimestamp = faciliooSyncStatus?.lastRun?.finishedAt || faciliooSyncStatus?.lastRun?.startedAt;
  const rows = data?.data ?? [];
  const totalRows = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const isFrameworkV2 =
    uiSettings?.management?.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));
  const fixedViewportListLayout = isFrameworkV2 && !isObjectFormActive;
  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, start + 2);
    const adjustedStart = Math.max(1, end - 2);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  }, [page, totalPages]);

  const openEditForm = (row: ObjectRow) => {
    setEditForm({
      id: row.id,
      objektnummer: row.objektnummer,
      objektname: row.objektname,
      strasse: row.strasse,
      strasse2: row.strasse2 ?? "",
      plz: row.plz,
      ort: row.ort,
      standard_stimmrechtsart: row.standard_stimmrechtsart ?? "",
      status: row.status
    });
  };

  useEffect(() => {
    if (!canUpdate) return;
    const navState = location.state as { editObjectId?: string } | null;
    const targetId = navState?.editObjectId;
    if (!targetId || !rows.length) return;
    const match = rows.find((row) => row.id === targetId);
    if (!match) return;
    openEditForm(match);
    window.history.replaceState({}, document.title);
  }, [canUpdate, location.state, rows]);

  const resetListFilters = () => {
    setSearch("");
    setStatus("aktiv");
    setDigitalOnly("");
    setSortBy("objektnummer");
    setSortDir("asc");
    setPage(1);
  };

  const renderSortIndicator = (field: ObjectSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const renderV2SortHeader = (field: ObjectSortField, label: ReactNode, icon: ReactNode) => (
    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort(field)}>
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
      {renderSortIndicator(field)}
    </button>
  );

  return (
    <div className={fixedViewportListLayout ? "resolutions-v2-page" : undefined}>
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4.9 8.2h14.2v10.9H4.9z" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.1 4.9h7.8v3.3H8.1zM8.2 12h2.6M13.2 12h2.6M8.2 15.4h2.6M13.2 15.4h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          ) : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Objekte</span>
              <span className="resolutions-v2-title-sub">Übersicht aller Objekte und Basisdaten</span>
            </span>
          ) : (
            <span>Objekte</span>
          )}
        </h2>
        {canCreate ? (
          <button className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""} onClick={() => setShowCreate((v) => !v)}>
            {isFrameworkV2 && !showCreate ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>{showCreate ? "Schließen" : "Neues Objekt"}</span>
          </button>
        ) : null}
      </section>

      {canCreate && showCreate ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Objekt anlegen</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Objektnummer*</label>
              <input value={form.objektnummer} onChange={(e) => setForm({ ...form, objektnummer: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Objektname*</label>
              <input value={form.objektname} onChange={(e) => setForm({ ...form, objektname: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Straße*</label>
              <input value={form.strasse} onChange={(e) => setForm({ ...form, strasse: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Straße 2</label>
              <input value={form.strasse2} onChange={(e) => setForm({ ...form, strasse2: e.target.value })} />
            </div>
            <div className="form-field">
              <label>PLZ*</label>
              <input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Ort*</label>
              <input value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Standard Stimmrechtsart</label>
              <select value={form.standard_stimmrechtsart} onChange={(e) => setForm({ ...form, standard_stimmrechtsart: e.target.value as StandardStimmrechtsartOption })}>
                <option value="">Automatisch (aus Einheiten)</option>
                <option value="kopf">Kopfstimmen</option>
                <option value="stimmrecht">Stimmrecht</option>
                <option value="mea">MEA</option>
              </select>
            </div>
            <div className="form-field">
              <label>Status*</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as StatusOption })}>
                <option value="">Status wählen*</option>
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button disabled={!canCreateForm || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? "Speichere..." : "Objekt speichern"}
            </button>
            {createError ? <span className="error-text">{createError}</span> : null}
          </div>
        </section>
      ) : null}

      {!showCreate && canUpdate && editForm ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Objekt bearbeiten</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Objektnummer*</label>
              <input value={editForm.objektnummer} onChange={(e) => setEditForm({ ...editForm, objektnummer: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Objektname*</label>
              <input value={editForm.objektname} onChange={(e) => setEditForm({ ...editForm, objektname: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Straße*</label>
              <input value={editForm.strasse} onChange={(e) => setEditForm({ ...editForm, strasse: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Straße 2</label>
              <input value={editForm.strasse2} onChange={(e) => setEditForm({ ...editForm, strasse2: e.target.value })} />
            </div>
            <div className="form-field">
              <label>PLZ*</label>
              <input value={editForm.plz} onChange={(e) => setEditForm({ ...editForm, plz: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Ort*</label>
              <input value={editForm.ort} onChange={(e) => setEditForm({ ...editForm, ort: e.target.value })} />
            </div>
            <div className="form-field">
              <label>Standard Stimmrechtsart</label>
              <select value={editForm.standard_stimmrechtsart} onChange={(e) => setEditForm({ ...editForm, standard_stimmrechtsart: e.target.value as StandardStimmrechtsartOption })}>
                <option value="">Automatisch (aus Einheiten)</option>
                <option value="kopf">Kopfstimmen</option>
                <option value="stimmrecht">Stimmrecht</option>
                <option value="mea">MEA</option>
              </select>
            </div>
            <div className="form-field">
              <label>Status</label>
              <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as "aktiv" | "inaktiv" })}>
                <option value="aktiv">Aktiv</option>
                <option value="inaktiv">Inaktiv</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button disabled={!canEdit || editMutation.isPending} onClick={() => editMutation.mutate()}>
              {editMutation.isPending ? "Speichere..." : "Änderungen speichern"}
            </button>
            <button onClick={() => setEditForm(null)}>Abbrechen</button>
            {editError ? <span className="error-text">{editError}</span> : null}
          </div>
        </section>
      ) : null}

      {!isObjectFormActive && isError ? <section className="panel">Fehler beim Laden der Objekte.</section> : null}
      {!isObjectFormActive && isFetching && !isFrameworkV2 ? <section className="panel">Objekte werden geladen...</section> : null}
      {!isObjectFormActive && hasNoObjects && lastSyncFailed ? (
        <section className="panel error-text">
          Keine Objektdaten vorhanden. Der letzte Facilioo-Sync ist fehlgeschlagen
          {lastSyncTimestamp ? ` (${new Date(lastSyncTimestamp).toLocaleString("de-DE")})` : ""}.
          Bitte Facilioo-Verbindung prüfen und Sync erneut starten.
        </section>
      ) : null}
      {!isObjectFormActive && actionError ? <section className="panel error-text">{actionError}</section> : null}
      {!isObjectFormActive ? (
        isFrameworkV2 ? (
          <section className="panel resolutions-v2-list-panel">
            <div className="resolutions-v2-filterbar">
              <div className="resolutions-v2-filter-fields">
                <label className="resolutions-v2-filter-input">
                  <span className="resolutions-v2-filter-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none">
                      <circle cx="9" cy="9" r="5.6" stroke="currentColor" strokeWidth="1.8" />
                      <path d="m13.1 13.1 3.4 3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </label>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                </select>
                <select value={digitalOnly} onChange={(e) => setDigitalOnly(e.target.value as DigitalOnlyOption)}>
                  <option value="">Alle Objekte</option>
                  <option value="1">Digitale Objekte</option>
                  <option value="0">Analoge Objekte</option>
                </select>
              </div>
              <button type="button" className="resolutions-v2-reset-btn" onClick={resetListFilters}>
                <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M5.1 6.3a6.3 6.3 0 1 1-1.3 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <path d="M4.9 3.9v3.4h3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Filter zurücksetzen
              </button>
            </div>

            <div className="resolutions-v2-row-count">Zeilen: {rows.length} / Gesamt: {totalRows}</div>

            <div className="resolutions-v2-table-wrap">
              <table className="resolutions-v2-table objects-v2-table">
                <colgroup>
                  <col style={{ width: "126px" }} />
                  <col style={{ width: "440px" }} />
                  <col style={{ width: "126px" }} />
                  <col />
                  <col style={{ width: "198px" }} />
                  <col style={{ width: "154px" }} />
                  <col style={{ width: "126px" }} />
                  <col style={{ width: "112px" }} />
                  <col style={{ width: "92px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
                            <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
                          </svg>
                        </span>
                        <span className="resolutions-v2-th-label">Objekt‑Nr</span>
                      </span>
                    </th>
                    <th>
                      {renderV2SortHeader(
                        "objektname",
                        "Objektname",
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M4.2 7.3h11.6v8.5H4.2z" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M6.8 4.7h6.4v2.6H6.8zM6.9 10.3h2.1M10.9 10.3H13M6.9 13.2h2.1M10.9 13.2H13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      )}
                    </th>
                    <th>
                      {renderV2SortHeader(
                        "anzahl_einheiten",
                        "Einheiten",
                        <svg viewBox="0 0 20 20" fill="none">
                          <rect x="4" y="3.9" width="12" height="12.2" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M7 7.4h2.1M10.9 7.4H13M7 10h2.1M10.9 10H13M7 12.6h2.1M10.9 12.6H13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      )}
                    </th>
                    <th>
                      {renderV2SortHeader(
                        "anzahl_eigentuemer",
                        "Eigentümer",
                        <svg viewBox="0 0 20 20" fill="none">
                          <circle cx="7.2" cy="7" r="2" stroke="currentColor" strokeWidth="1.7" />
                          <circle cx="12.8" cy="7" r="2" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M3.8 14.6a4.2 4.2 0 0 1 6.8 0M9.4 14.6a4.2 4.2 0 0 1 6.8 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      )}
                    </th>
                    <th>
                      {renderV2SortHeader(
                        "anzahl_nicht_digital_erreichbar",
                        "Nicht digital",
                        <svg viewBox="0 0 20 20" fill="none">
                          <rect x="5.4" y="2.8" width="9.2" height="14.4" rx="1.9" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M8.1 5.8h3.8M7.4 14.1l5.2-5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      )}
                    </th>
                    <th>
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <path d="M4.1 15.9h11.8M5.6 15.9V9.4M10 15.9V6.7M14.4 15.9V4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          </svg>
                        </span>
                        <span className="resolutions-v2-th-label">Stimmrechte</span>
                      </span>
                    </th>
                    <th>
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <path d="M10 3.7 15.3 6.8v6.4L10 16.3l-5.3-3.1V6.8L10 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                            <path d="M10 3.7v12.6M4.7 6.8 10 10l5.3-3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <span className="resolutions-v2-th-label">MEA</span>
                      </span>
                    </th>
                    <th>
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
                            <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
                          </svg>
                        </span>
                        <span className="resolutions-v2-th-label">Status</span>
                      </span>
                    </th>
                    <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">
                      <span className="resolutions-v2-th-plain">
                        <span className="resolutions-v2-th-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
                            <circle cx="10" cy="10" r="1.4" fill="currentColor" />
                            <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
                          </svg>
                        </span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                      const locked = lockedObjectIds.has(r.id);
                      const menuId = r.id;
                      return (
                      <tr key={r.id}>
                        <td>
                          <Link to={`/objects/${r.id}`} className="badge object-number" style={{ textDecoration: "none" }}>
                            {r.objektnummer}
                          </Link>
                        </td>
                        <td className="resolutions-v2-object-cell">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span>{r.objektname}</span>
                            {Number(r.anzahl_nicht_digital_erreichbar ?? 0) === 0 ? (
                              <span className="badge aktiv" style={{ fontSize: 11, padding: "2px 8px" }}>100 % digital</span>
                            ) : null}
                          </span>
                        </td>
                        <td>{r.anzahl_einheiten ?? "-"}</td>
                        <td>{r.anzahl_eigentuemer ?? "-"}</td>
                        <td>{r.anzahl_nicht_digital_erreichbar ?? "-"}</td>
                        <td>{Number(r.stimmrecht_summe ?? 0) > 0 ? formatNumberDE(Number(r.stimmrecht_summe ?? 0)) : "-"}</td>
                        <td>{Number(r.mea_summe ?? 0) > 0 ? formatNumberDE(Number(r.mea_summe ?? 0)) : "-"}</td>
                        <td><span className={`badge ${r.status}`}>{r.status}</span></td>
                        <td>
                          <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="resolutions-v2-action-trigger"
                              aria-label={`Aktionen für Objekt ${r.objektnummer}`}
                              aria-expanded={openActionMenuId === menuId}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openActionMenuId === menuId) {
                                  setOpenActionMenuId(null);
                                  return;
                                }
                                openActionMenuAtTrigger(menuId, e.currentTarget, canUpdate && canDelete ? 96 : 52);
                              }}
                            >
                              ⋮
                            </button>
                            {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                              <div
                                className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseEnter={() => setIsActionMenuHovered(true)}
                                onMouseLeave={() => setIsActionMenuHovered(false)}
                              >
                                {canUpdate ? (
                                  <button
                                    type="button"
                                    disabled={locked}
                                    onClick={() => {
                                      if (locked) {
                                        setActionError("Objekt ist wegen aktivem Umlaufbeschluss gesperrt.");
                                        setOpenActionMenuId(null);
                                        return;
                                      }
                                      openEditForm(r);
                                      setOpenActionMenuId(null);
                                    }}
                                  >
                                    Bearbeiten
                                  </button>
                                ) : null}
                                {canDelete ? (
                                  <button
                                    type="button"
                                    disabled={locked || deleteMutation.isPending}
                                    onClick={() => {
                                      if (locked) {
                                        setActionError("Objekt ist wegen aktivem Umlaufbeschluss gesperrt.");
                                        setOpenActionMenuId(null);
                                        return;
                                      }
                                      setDeleteConfirm({
                                        id: r.id,
                                        label: `${r.objektnummer} - ${r.objektname}`,
                                        code: String(Math.floor(1000 + Math.random() * 9000)),
                                        input: ["", "", "", ""],
                                        error: ""
                                      });
                                      setOpenActionMenuId(null);
                                    }}
                                  >
                                    Löschen
                                  </button>
                                ) : null}
                              </div>,
                              document.body
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="resolutions-v2-pagination">
              <div className="resolutions-v2-page-size">
                <select
                  value={String(pageSize)}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setPageSize(Number.isFinite(next) && next > 0 ? next : 10);
                  }}
                >
                  <option value="10">10 pro Seite</option>
                  <option value="20">20 pro Seite</option>
                  <option value="50">50 pro Seite</option>
                </select>
              </div>
              <div className="resolutions-v2-page-center">
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1}>
                  ‹
                </button>
                {pageItems.map((pageItem) => (
                  <button
                    key={pageItem}
                    type="button"
                    className={`resolutions-v2-page-chip ${pageItem === page ? "active" : ""}`}
                    onClick={() => setPage(pageItem)}
                  >
                    {pageItem}
                  </button>
                ))}
                <button
                  type="button"
                  className="resolutions-v2-page-arrow"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  ›
                </button>
                <span className="resolutions-v2-page-info">Seite {page} von {totalPages}</span>
              </div>
              <div className="resolutions-v2-page-next-wrap">
                <button
                  type="button"
                  className="resolutions-v2-next-btn"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                >
                  <span>Weiter</span>
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </section>
        ) : (
          <ServerDataTable
            columns={columns}
            rows={rows}
            total={totalRows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            bodyMaxHeight={560}
            topContent={
              <section className="filterbar" style={{ margin: 0 }}>
                <input placeholder="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={digitalOnly} onChange={(e) => setDigitalOnly(e.target.value as DigitalOnlyOption)}>
                  <option value="">Alle Objekte</option>
                  <option value="1">Digitale Objekte</option>
                  <option value="0">Analoge Objekte</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                </select>
              </section>
            }
          />
        )
      ) : null}

      <DeleteCodeConfirmModal
        open={!!deleteConfirm}
        message={deleteConfirm ? `Objekt ${deleteConfirm.label} soll gelöscht werden.` : ""}
        code={deleteConfirm?.code ?? ""}
        input={deleteConfirm?.input ?? ["", "", "", ""]}
        error={deleteConfirm?.error}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onInputChange={(next) => {
          if (!deleteConfirm) return;
          setDeleteConfirm({ ...deleteConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!deleteConfirm || !deleteConfirm.error) return;
          setDeleteConfirm({ ...deleteConfirm, error: "" });
        }}
        onConfirm={() => {
          if (!deleteConfirm) return;
          const enteredCode = deleteConfirm.input.join("");
          if (enteredCode !== deleteConfirm.code) {
            setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          deleteMutation.mutate(deleteConfirm.id);
        }}
      />
    </div>
  );
};



